import { Backdrop, Fade } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { modalStyles } from '../../../ModalStyles'
import { ModalStyled } from '../../../Modals.styled'
import { RootState } from '../../../../../setup'
import { checkErrorAPICall } from '../../../../helpers/checkErrorAPICall'
import { actionTypes } from '../../../../modules/auth/redux/PagesRedux'
import { Dimensioner, FreightSnapScan } from '../../../../helpers/FreightSnap/types'
import { freightSnapScan, getFreightSnapFile, freightSnapErrorMessage } from '../../../../helpers/FreightSnap/freightSnapService'
import { CheckIcon, CloseIcon, ScanIcon } from './DimensionerIcons'
import DimensionerScanControls from './DimensionerScanControls'
import MeasurementGrid from './MeasurementGrid'
import ScanFiles from './ScanFiles'

interface Props {
    open: boolean
    setOpen: (open: boolean) => void
    dimensioners: Dimensioner[]
    defaultDimensionerId?: string | number
    locationId?: string | number
    BOLNumber?: string
    initialScan?: FreightSnapScan | null
    freightInfoUOM?: {value?: string}
    onApply: (scan: FreightSnapScan, dimensionerId?: string | number) => void
}

const DimensionerModal = ({open, setOpen, dimensioners, defaultDimensionerId, locationId, BOLNumber, initialScan, freightInfoUOM, onApply}: Props) => {
    const dispatch = useDispatch()
    const fontSize = useSelector<RootState>(({pages}) => pages.fontSize) as number
    const classesModal = modalStyles({fontSize: fontSize})
    const [selectedId, setSelectedId] = useState<string>('')
    const [scan, setScan] = useState<FreightSnapScan | null>(null)
    const [loading, setLoading] = useState(false)
    // Error from the last scan, shown inside the window instead of leaving it on "Awaiting scan"
    // (Joaquin, ticket 31017813).
    const [scanError, setScanError] = useState<string | null>(null)
    const scanToken = useRef(0)

    useEffect(() => {
        if(open){
            const preselect = defaultDimensionerId ?? (dimensioners.length === 1 ? dimensioners[0].dimensionerId : '')
            setSelectedId(preselect ? String(preselect) : '')
            setScan(initialScan ?? null)
            setScanError(null)
        }
    }, [open, defaultDimensionerId, dimensioners, initialScan])

    const handleClose = () => {
        scanToken.current++
        setLoading(false)
        setOpen(false)
    }

    const onSelect = (value: string) => {
        setSelectedId(value)
        setScan(null)
        setScanError(null)
    }

    const runScan = () => {
        if(!selectedId) return
        const token = ++scanToken.current
        setScanError(null)
        setLoading(true)
        freightSnapScan({locationId, dimensionerId: selectedId, BOLNumber})
            .then((result) => {
                if(scanToken.current !== token) return
                setScan(result)
                setLoading(false)
            })
            .catch((error) => {
                if(scanToken.current !== token) return
                setLoading(false)
                const friendly = freightSnapErrorMessage(error)
                setScanError(friendly || 'The scan failed. Please try again.')
                if(!friendly){
                    checkErrorAPICall(error, dispatch, 'FreightSnap')
                }
            })
    }

    const handleOk = () => {
        if(scan){
            onApply(scan, selectedId)
        }
        handleClose()
    }

    const downloadFile = (kind: 'images' | 'certificate') => {
        if(!scan?.id) return
        getFreightSnapFile(scan.id, kind)
            .then((blob) => {
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `freightsnap-${kind}-${scan.id}.pdf`
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
            })
            .catch((error) => checkErrorAPICall(error, dispatch, 'FreightSnap'))
    }

    const metric = !!(freightInfoUOM && freightInfoUOM.value && freightInfoUOM.value !== 'US')
    const uom = metric ? 'cm' : (scan?.dimensionUom || 'in')
    const wUom = metric ? 'kg' : (scan?.weightUom || 'lbs')
    const hasData = !!scan
    const needsScan = !!selectedId && !scan && !loading && !scanError

    return (
        <ModalStyled
            aria-labelledby="freightsnap-dimensioner-title"
            className={classesModal.modal}
            fontSize={fontSize}
            open={open}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{timeout: 500}}
        >
            <Fade in={open}>
                <div className={classesModal.paperSaveQuote} style={{visibility: 'visible', opacity: '100%', maxWidth: 480, padding: 0, borderRadius: 8, overflow: 'hidden'}}>
                    <div className="d-flex align-items-center justify-content-between" style={{padding: '18px 18px 0'}}>
                        <div className="d-flex align-items-center">
                            <div className="d-flex align-items-center justify-content-center me-3" style={{width: 42, height: 42, borderRadius: 8, background: '#F1FAFF', color: '#009EF7'}}>
                                <ScanIcon size={21}/>
                            </div>
                            <div>
                                <div style={{fontSize: 17, fontWeight: 600, color: '#181C32', lineHeight: 1.15}}>Dimensioner</div>
                                <div className="text-uppercase" style={{fontSize: 10, fontWeight: 600, letterSpacing: '.08em', color: '#A1A5B7', marginTop: 2}}>FreightSnap scan</div>
                            </div>
                        </div>
                        <button type="button" onClick={handleClose} className="d-flex align-items-center justify-content-center" style={{width: 32, height: 32, border: 'none', background: '#F5F8FA', borderRadius: 7, color: '#7E8299', cursor: 'pointer'}}>
                            <CloseIcon size={15}/>
                        </button>
                    </div>

                    <div style={{padding: '17px 18px 0'}}>
                        <DimensionerScanControls
                            dimensioners={dimensioners}
                            selectedId={selectedId}
                            loading={loading}
                            needsScan={needsScan}
                            hasData={hasData}
                            onSelect={onSelect}
                            onScan={runScan}
                        />
                        <MeasurementGrid scan={scan} hasData={hasData} uom={uom} wUom={wUom} metric={metric} error={scanError}/>
                        <ScanFiles
                            imageCount={scan?.imageCount ?? 0}
                            imagesDisabled={!hasData}
                            certificateDisabled={!hasData}
                            onDownloadImages={() => downloadFile('images')}
                            onDownloadCertificate={() => downloadFile('certificate')}
                        />
                    </div>

                    <div className="d-flex align-items-center justify-content-end" style={{gap: 9, padding: '16px 18px', borderTop: '1px solid #EFF2F5', marginTop: 16}}>
                        <button type="button" className="btn btn-light" style={{height: 42}} onClick={handleClose}>Close</button>
                        <button type="button" className="btn btn-primary d-flex align-items-center" style={{height: 42}} disabled={!hasData} onClick={handleOk}>
                            <CheckIcon size={15}/><span className="ms-2">Apply</span>
                        </button>
                    </div>
                </div>
            </Fade>
        </ModalStyled>
    )
}

export default DimensionerModal
